import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { canWrite } from '../../utils/roles';

const packCode = pack => pack.code || `P-${String(pack.id).padStart(4, '0')}`;

function PackForm({ pack, onClose, onSaved }) {
  const [name, setName] = useState(pack?.name || '');
  const [category, setCategory] = useState(pack?.category || '');
  const [notes, setNotes] = useState(pack?.notes || '');
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(() => new Set((pack?.items || []).map(item => item.id)));
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/inventory/access-packs/available-items').then(result => setItems(result.data)).catch(() => setItems([]));
  }, []);

  const visible = useMemo(() => {
    const words = search.trim().toLocaleLowerCase('es').split(/\s+/).filter(Boolean);
    return items.filter(item => words.every(word => `${item.name} ${item.unit_label || ''}`.toLocaleLowerCase('es').includes(word)));
  }, [items, search]);

  const toggle = id => setSelected(previous => {
    const next = new Set(previous);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const submit = async event => {
    event.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      const payload = { name: name.trim(), category: category.trim() || null, notes: notes.trim() || null, item_ids: [...selected] };
      if (pack) await api.put(`/inventory/access-packs/${pack.id}`, payload);
      else await api.post('/inventory/access-packs', payload);
      onSaved();
    } catch (error) {
      alert(error.response?.data?.error || 'No se pudo guardar el pack.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <form className="modal inv-access-pack-modal" onClick={event => event.stopPropagation()} onSubmit={submit}>
        <div className="modal-title">{pack ? 'Editar pack' : 'Nuevo pack'}</div>
        <p className="text-dim text-sm">Un pack no retira unidades del stock individual: es un acceso directo reutilizable.</p>
        <div className="form-group"><label className="form-label">Nombre</label><input className="input" autoFocus value={name} onChange={event => setName(event.target.value)} placeholder="Ej. Kit conferencia" /></div>
        <div className="form-group"><label className="form-label">Categoría</label><input className="input" value={category} onChange={event => setCategory(event.target.value)} placeholder="Opcional" /></div>
        <div className="form-group"><label className="form-label">Notas</label><textarea className="input" rows="2" value={notes} onChange={event => setNotes(event.target.value)} placeholder="Opcional" /></div>
        <div className="form-label" style={{ marginBottom: 8 }}>Unidades incluidas ({selected.size})</div>
        <input className="input" value={search} onChange={event => setSearch(event.target.value)} placeholder="Buscar artículo o etiqueta..." />
        <div className="inv-access-pack-items">
          {visible.map(item => <label key={item.id} className="inv-access-pack-item">
            <input type="checkbox" checked={selected.has(item.id)} onChange={() => toggle(item.id)} />
            <span><strong>{item.name}</strong>{item.unit_label ? ` · ${item.unit_label}` : ''}</span>
          </label>)}
        </div>
        <div className="modal-actions"><button type="button" className="btn btn-ghost" onClick={onClose}>Cancelar</button><button className="btn btn-primary" disabled={saving}>{saving ? 'Guardando…' : 'Guardar pack'}</button></div>
      </form>
    </div>
  );
}

export default function AccessPacksPage() {
  const { user } = useAuth();
  const canEdit = canWrite(user, 'inventario.packs');
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(undefined);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setPacks((await api.get('/inventory/access-packs')).data); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const rows = useMemo(() => {
    const words = search.trim().toLocaleLowerCase('es').split(/\s+/).filter(Boolean);
    return packs.filter(pack => words.every(word => `${pack.name} ${pack.category || ''} ${pack.notes || ''}`.toLocaleLowerCase('es').includes(word)));
  }, [packs, search]);

  const remove = async () => {
    await api.delete(`/inventory/access-packs/${deleteTarget.id}`);
    setDeleteTarget(null);
    load();
  };

  return <div className="inventory-page inventory-page--wide">
    <div className="page-header"><div><h1 className="page-title">Packs</h1><p className="page-subtitle">Grupos reutilizables de unidades, sin bloquear su selección individual.</p></div>
      {canEdit && <button className="btn btn-primary" onClick={() => setEditing(null)}>+ Nuevo pack</button>}</div>
    <div className="inv-toolbar"><div className="inv-search-wrapper"><input className="input" value={search} onChange={event => setSearch(event.target.value)} placeholder="Buscar pack..." /></div><span className="text-dim text-sm">{rows.length} packs</span></div>
    <div className="inv-table">
      <div className="inv-table-head inv-pack-table-head"><span>Código</span><span>Pack</span><span>Categoría</span><span>Unidades</span><span>Notas</span><span /></div>
      {loading ? <div className="inv-table-empty">Cargando packs...</div> : rows.length === 0 ? <div className="inv-table-empty">No hay packs todavía.</div> : rows.map(pack => <div className="inv-table-row inv-pack-table-row" key={pack.id}>
        <span className="inv-code">{packCode(pack)}</span><strong>{pack.name}</strong><span>{pack.category || '—'}</span><span>{pack.item_count} unidades</span><span className="inv-table-notes">{pack.notes || '—'}</span>
        {canEdit && <span className="inv-table-actions"><button className="btn btn-ghost btn-sm" onClick={() => setEditing(pack)}>Editar</button><button className="btn btn-ghost btn-sm" onClick={() => setDeleteTarget(pack)}>Eliminar</button></span>}
      </div>)}
    </div>
    {editing !== undefined && <PackForm pack={editing} onClose={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); load(); }} />}
    {deleteTarget && <div className="modal-overlay" onClick={() => setDeleteTarget(null)}><div className="modal" onClick={event => event.stopPropagation()}><div className="modal-title">Eliminar pack</div><p>¿Eliminar <strong>{deleteTarget.name}</strong>? Las unidades no se eliminan ni cambian de disponibilidad.</p><div className="modal-actions"><button className="btn btn-ghost" onClick={() => setDeleteTarget(null)}>Cancelar</button><button className="btn btn-danger" onClick={remove}>Eliminar</button></div></div></div>}
  </div>;
}
